import { useState, useEffect, useContext } from 'react';
import { useCustomise } from '../../../context/customiseContext';
import { Layout, Row, Col, Form, Select, Tooltip, Button } from 'antd-v4';
import { motion } from 'framer-motion';
import { SignatureFilled } from '@ant-design/icons';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import MenuLogo from './logo';
import MenuFooter from './footer';
import MenuItem from './item';
import MenuMobile from './mobile';

// Context
import { FirebaseContext } from '../../../context/firebaseContext';
import FeedbackModal from '../../../view/pages/conversations/components/FeedbackModal';
import { BackendContext } from '../../../context/backendContext';

const { Sider } = Layout;

export default function Sidebar(props) {
  const { visible, setVisible } = props;

  // Customise Context
  const { sidebarCollapsed, sidebarCollapseButton } = useCustomise();
  const { version, putUser } = useContext(BackendContext);

  // Hooks
  const { userData, subscriptionId, getSubscriptionsByClaims, _setSubscription, getAIByApiKey } =
    useContext(FirebaseContext);

  // States
  const [collapsed, setCollapsed] = useState(false);
  const [subscriptions, setSubscriptions] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [, setLastLogin] = useState(null);
  const [displayFeedbackForm, setDisplayFeedbackForm] = useState(false);

  // Function to track and update `last_login`
  const trackAndUpdateLastLogin = async () => {
    try {
      if (!userData) return;

      const currentTime = Date.now();
      const lastHomepageVisit = localStorage.getItem('leedpulse-lastlogin-' + userData.uid);

      // If more than 24h, we update `localStorage` in Firebase
      if (!lastHomepageVisit || currentTime - parseInt(lastHomepageVisit) > 24 * 60 * 60 * 1000) {
        localStorage.setItem('leedpulse-lastlogin-' + userData.uid, currentTime.toString());

        if (userData) {
          const response = await putUser(
            userData.uid,
            { last_login: currentTime },
            activeSubscription.api_key,
            false
          );
          if (response.success) {
            setLastLogin(new Date(currentTime).toLocaleString());
          } else {
            console.error('Erreur lors de la mise à jour de last_login :', response.msg);
          }
        }
      } else {
        // console.log("Moins de 24 heures depuis la dernière mise à jour.");
      }
    } catch (error) {
      console.error('Erreur lors du tracking de last_login :', error);
      return;
    }
  };

  useEffect(() => {
    if (userData && activeSubscription) {
      // Vrai effet de bord : trackAndUpdateLastLogin ecrit last_login dans Firestore.
      // Rien a deriver ici, c'est une ecriture au montage.
      // eslint-disable-next-line react/set-state-in-effect
      trackAndUpdateLastLogin();
    }
  }, [userData, activeSubscription]);

  useEffect(() => {
    if (userData) {
      getSubscriptionsByClaims().then((data) => {
        if (!data?.doc || !Array.isArray(data.doc) || data.doc.length === 0)
          return console.log('No subscription found');
        setSubscriptions(data.doc);
      });
    }
  }, [userData]);

  useEffect(() => {
    if (!subscriptions || subscriptions.length === 0) return;

    const subscription = subscriptions.find((subscription) => subscription.id === subscriptionId);

    if (subscription) {
      // La branche else appelle changeSubscription, qui change l'URL : le calcul et
      // la navigation partagent la meme condition. Les separer ferait naviguer
      // pendant le rendu.
      // eslint-disable-next-line react/set-state-in-effect
      setActiveSubscription(subscription);
    } else {
      changeSubscription(subscriptions[0].name);
    }
  }, [subscriptions]);

  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia('(max-width: 992px)').matches) {
        setCollapsed(false);
      } else {
        setCollapsed(sidebarCollapsed);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [sidebarCollapsed]);

  // Mobile Sidebar
  const onClose = () => {
    setVisible(false);
  };

  // Menu
  function toggle() {
    localStorage.setItem('leedpulse-sidebarCollapsed', !collapsed);
    setCollapsed(!collapsed);
  }

  async function changeSubscription(value) {
    console.log(value);
    console.log(subscriptions);
    const subscription = subscriptions.find((subscription) => subscription.name === value);
    setActiveSubscription(subscription);
    _setSubscription(subscription.id, subscription.api_key);

    // Local storage of subscription ID & reload page
    localStorage.setItem('leedpulse-subscription', JSON.stringify(subscription));
    localStorage.setItem('leedpulse-subscriptionId', subscription.id);

    // Get AI document from subscription ID
    const oAI = await getAIByApiKey(subscription.api_key);
    localStorage.setItem('leedpulse-ai', JSON.stringify(oAI.doc));
    window.location.reload();
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={316}
      className="sidebar-home hp-sidebar hp-border-right-1 hp-border-color-black-40 hp-border-color-dark-80"
    >
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: 'spring', duration: 0.5, delay: 0.1 }}
        className="hp-d-flex hp-h-100"
        style={{ flexDirection: 'column' }}
      >
        <Row align="bottom" justify="space-between">
          <Col>
            {collapsed === false ? (
              <>
                <MenuLogo onClose={onClose} />
                <p
                  style={{ marginLeft: '0.5rem' }}
                  className="hp-caption hp-font-weight-400 hp-text-color-primary-1"
                >
                  v{version}
                </p>
              </>
            ) : (
              ''
            )}
          </Col>

          {sidebarCollapseButton &&
            (collapsed === false ? (
              <Col className="hp-pr-0">
                <Tooltip title="Réduire le menu" placement="right">
                  <div
                    className="hp-cursor-pointer hp-sidebar-collapse-button hp-border-radius-round hp-p-4"
                    onClick={toggle}
                  >
                    <RiArrowLeftSLine size={20} className="hp-text-color-primary-1" />
                  </div>
                </Tooltip>
              </Col>
            ) : (
              <Col span={24} className="hp-d-flex-full-center">
                <Tooltip title="Agrandir le menu" placement="right">
                  <div
                    className="hp-cursor-pointer hp-sidebar-collapse-button hp-border-radius-round hp-p-4"
                    onClick={toggle}
                  >
                    <RiArrowRightSLine size={20} className="hp-text-color-primary-1" />
                  </div>
                </Tooltip>
              </Col>
            ))}

          {collapsed && (
            <Col span={24} className="hp-mt-12 hp-d-flex-full-center">
              <MenuLogo onClose={onClose} small={true} />
            </Col>
          )}
        </Row>

        <MenuItem onClose={onClose} />

        {/* Help button */}
        {collapsed ? (
          <Tooltip title="Nous contacter" placement="right">
            <Button
              icon={<SignatureFilled className="hp-mr-6" />}
              type="text"
              style={{
                marginBottom: 15,
                marginLeft: '1rem',
                marginRight: '1rem',
                width: 'auto',
                color: '#6737ff',
              }}
              onClick={() => {
                setDisplayFeedbackForm(true);
              }}
            />
          </Tooltip>
        ) : (
          <Button
            icon={<SignatureFilled className="hp-mr-6" />}
            type="text"
            style={{ marginBottom: 15, color: '#6737ff' }}
            onClick={() => {
              setDisplayFeedbackForm(true);
            }}
          >
            Nous contacter
          </Button>
        )}

        {/* Subsription switcher */}
        {activeSubscription && (
          <Form.Item>
            <Select
              disabled={subscriptions.length < 2}
              showSearch
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              dropdownAlign={{
                points: ['bl', 'tl'],
                offset: [0, 4],
              }}
              defaultValue={activeSubscription?.name}
              onChange={async (value) => {
                changeSubscription(value);
              }}
            >
              {subscriptions &&
                subscriptions.map((subscription) => (
                  <Select.Option key={subscription.id} value={subscription.name}>
                    {subscription.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        )}

        <MenuFooter onClose={onClose} collapsed={collapsed} />

        <MenuMobile onClose={onClose} visible={visible} />
        <FeedbackModal
          title={"J'ai besoin d'aide"}
          visible={displayFeedbackForm}
          onOk={() => setDisplayFeedbackForm(false)}
          onCancel={() => setDisplayFeedbackForm(false)}
        />
      </motion.div>
    </Sider>
  );
}
